import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { SwipeCard } from "./SwipeCard";
import { MatchModal } from "./MatchModal";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import { mockJobs } from "@/data/mockJobs";

// Number of cards to render in the stack
const STACK_SIZE = 3;

interface SwipeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SwipeModal({ isOpen, onClose }: SwipeModalProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMatch, setShowMatch] = useState(false);
  const [likedJobs, setLikedJobs] = useState<string[]>([]);
  const [mounted, setMounted] = useState(false);

  // Ensure component is mounted before rendering portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent body scroll when modal is open and set theme color
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#000000");
      }
    } else {
      document.body.style.overflow = "";
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#0E0E0F");
      }
    }

    return () => {
      document.body.style.overflow = "";
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#0E0E0F");
      }
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const currentJob = mockJobs[currentIndex];
  const hasMoreJobs = currentIndex < mockJobs.length;

  const handleSwipeLeft = useCallback(() => {
    if (!hasMoreJobs) return;
    setCurrentIndex((prev) => prev + 1);
  }, [hasMoreJobs]);

  const handleSwipeRight = useCallback(() => {
    if (!hasMoreJobs) return;
    setLikedJobs((prev) => [...prev, currentJob.id]);

    const isMatch = Math.random() > 0.6;
    if (isMatch) {
      setShowMatch(true);
    }

    setCurrentIndex((prev) => prev + 1);
  }, [currentJob, hasMoreJobs]);

  const { isDragging, isAnimatingOut, handlers, getCardStyle, getOverlayOpacity } = useSwipeGesture(
    {
      onSwipeLeft: handleSwipeLeft,
      onSwipeRight: handleSwipeRight,
      threshold: 100,
    }
  );

  const handleClose = useCallback(() => {
    setCurrentIndex(0);
    setLikedJobs([]);
    setShowMatch(false);
    onClose();
  }, [onClose]);

  if (!mounted || !isOpen) return null;

  // Get the cards to render in the stack
  const stackCards = mockJobs.slice(currentIndex, currentIndex + STACK_SIZE);

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black"
      style={{
        animation: "fadeIn 0.3s ease-out",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        marginTop: "calc(-1 * env(safe-area-inset-top))",
        marginBottom: "calc(-1 * env(safe-area-inset-bottom))",
        marginLeft: "calc(-1 * env(safe-area-inset-left))",
        marginRight: "calc(-1 * env(safe-area-inset-right))",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Progress indicator */}
      <div className="absolute left-0 right-0 top-0 z-20 px-4 pt-4">
        <div className="flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / mockJobs.length) * 100}%` }}
            />
          </div>
          <span className="text-sm font-medium text-white">
            {currentIndex + 1} / {mockJobs.length}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div
        className="-mt-16 flex h-full w-full items-center justify-center px-4"
        style={{ animation: "slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
      >
        {stackCards.length > 0 ? (
          <div
            className="relative w-full max-w-md"
            style={{ height: "clamp(400px, calc(100vh - 200px), 600px)" }}
          >
            {/* Render cards behind (scaled down, blurred, no gesture handling) */}
            {stackCards
              .slice(1)
              .reverse()
              .map((job, reversedIndex) => {
                const stackIndex = stackCards.length - 1 - reversedIndex;
                return (
                  <div
                    key={job.id}
                    className="pointer-events-none absolute inset-0"
                    style={{
                      zIndex: STACK_SIZE - stackIndex,
                      transform: "scale(0.5)",
                      filter: "blur(4px)",
                    }}
                  >
                    <SwipeCard job={job} />
                  </div>
                );
              })}

            {/* Top card with gesture handling */}
            <div key={stackCards[0].id} className="absolute inset-0" style={{ zIndex: STACK_SIZE }}>
              <div
                className="relative h-full"
                {...handlers}
                style={{
                  ...getCardStyle(),
                  animation:
                    !isDragging && !isAnimatingOut
                      ? "cardEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards"
                      : "none",
                }}
              >
                <SwipeCard
                  job={stackCards[0]}
                  onSwipe={() => {}}
                  onNope={handleSwipeLeft}
                  onYes={handleSwipeRight}
                  isDragging={isDragging}
                />

                {/* Swipe overlays */}
                <div
                  className="pointer-events-none absolute left-8 top-20"
                  style={{ opacity: getOverlayOpacity("left") }}
                >
                  <div className="rotate-[-20deg] rounded-2xl border-4 border-white bg-[#2d3142] px-6 py-3">
                    <span className="text-2xl font-bold text-white">NOPE</span>
                  </div>
                </div>
                <div
                  className="pointer-events-none absolute right-8 top-20"
                  style={{ opacity: getOverlayOpacity("right") }}
                >
                  <div className="rotate-[20deg] rounded-2xl border-4 border-white bg-[#4ADE80] px-6 py-3">
                    <span className="text-2xl font-bold text-white">LIKE</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <h2 className="mb-2 text-2xl font-bold text-white">Ingen flere stillinger</h2>
            <p className="mb-6 text-gray-400">Du har sett {likedJobs.length} stillinger!</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  setCurrentIndex(0);
                  setLikedJobs([]);
                }}
                className="rounded-full bg-[#FF3B5C] px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90"
              >
                Start på nytt
              </button>
              <button
                onClick={handleClose}
                className="rounded-full bg-white/10 px-6 py-3 font-semibold text-white transition-colors hover:bg-white/20"
              >
                Lukk
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Match modal */}
      {showMatch && currentJob && (
        <MatchModal
          job={currentJob}
          onClose={() => setShowMatch(false)}
          onStartChat={() => {
            setShowMatch(false);
            handleClose();
            navigate(`/chat/${currentJob.id}`);
          }}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
